import { Sun, Battery, Wrench, Zap, Pickaxe, Map, Users, Factory } from "lucide-react";
import SEO from "@/components/SEO";
import ScopeRail from "@/components/ScopeRail";
import CTABanner from "@/components/CTABanner";

const SECTIONS = [
  {
    id: "solar-ic", icon: Sun, title: "Solar Plant Installation & Commissioning",
    intro: "Complete I&C for ground-mount and rooftop solar PV — from structure to synchronisation.",
    list: [
      "Module mounting structure (MMS) erection and alignment",
      "PV module installation and clamping with torque records",
      "DC stringing, connectorisation and cable dressing",
      "Inverter, SCB/string-combiner and LT panel installation",
      "AC cabling, terminations and ICR works",
      "Earthing grid and lightning-arrestor installation",
      "Pre-commissioning checks: IR/megger testing, polarity, continuity",
      "Testing & commissioning support up to plant synchronisation",
    ],
  },
  {
    id: "bess", icon: Battery, title: "BESS Installation & Commissioning",
    intro: "Disciplined installation and commissioning support for battery energy storage and hybrid plants.",
    list: [
      "Foundation and container/skid placement support",
      "PCS, transformer and auxiliary equipment installation",
      "DC battery terminations with ferruling and torque control",
      "Control, communication and auxiliary power cabling",
      "Earthing and safety-system installation",
      "Pre-charging checks and commissioning assistance with OEM teams",
    ],
  },
  {
    id: "epc", icon: Wrench, title: "EPC / Balance of Plant",
    intro: "End-to-end BoP execution for utility-scale and C&I projects — one accountable contractor for civil, mechanical and electrical.",
    list: [
      "Engineering coordination and constructability inputs",
      "Procurement assistance for BoS materials",
      "Civil works: piling, foundations, equipment plinths, trenches, internal roads and fencing",
      "Mechanical erection of structures and equipment",
      "Complete electrical BoP",
      "Testing, commissioning and documented handover",
    ],
  },
  {
    id: "electrical", icon: Zap, title: "Electrical Contracting",
    intro: "Licensed electrical contracting led by an expert electrical contractor — LT/HT works done right and proven by test records.",
    list: [
      "LT/HT electrical installations and switchyard works",
      "Transformer installation, oil filtration and charging support",
      "SVG (Static Var Generator) installation and harmonic solutions",
      "Power and control cabling, glanding, ferruling and terminations",
      "Earthing systems and testing",
      "Megger/IR testing, CT/PT and protection support",
      "Electrical operations & maintenance",
    ],
  },
  {
    id: "earthwork", icon: Pickaxe, title: "Earth Work & Piling",
    intro: "Owned earthmoving and piling fleet for the most schedule-critical phase of any solar project.",
    list: [
      "Land clearing, levelling and grading",
      "Excavation and backfilling",
      "Cable trenching",
      "Pile foundation drilling with DTH rig, hydraulic auger and JCB-mounted piling machine — including hard and rocky strata",
      "Pile grouting with on-site AJAX self-loading concrete mixer",
      "Foundations and plinths",
    ],
  },
  {
    id: "land", icon: Map, title: "Land Liaison Services",
    intro: "On-ground support where land work actually happens — talukas, tehsils and revenue offices of Rajasthan's solar belt.",
    list: [
      "Land identification and feasibility support",
      "Khasra-level documentation and revenue-record coordination",
      "Local stakeholder and right-of-way liaison",
      "Support during survey, demarcation and possession",
    ],
  },
  {
    id: "manpower", icon: Users, title: "Manpower & Material Supply",
    intro: "Skilled crews and site materials, mobilised fast and housed in our own campers.",
    list: [
      "Skilled and semi-skilled manpower — electricians, fitters, riggers, helpers",
      "Experienced site supervisors and safety officers",
      "Tools, tackles and consumables",
      "Self-sufficient accommodation via company-owned site campers for remote locations",
    ],
  },
  {
    id: "industrial", icon: Factory, title: "Industrial & Infrastructure Support",
    intro: "Electrical and civil execution support beyond solar — for industrial plants and infrastructure works.",
    list: [
      "Industrial electrical installations and maintenance",
      "Civil and structural execution support",
      "Shutdown and project-based manpower deployment",
    ],
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Services | Yaduraj Enterprises"
        description="Solar & BESS I&C, EPC balance-of-plant, licensed electrical contracting, earth work & piling, land liaison and manpower — executed end-to-end."
      />
      <section className="ye-page-hero">
        <div className="ye-container">
          <span className="ye-eyebrow">Our Services</span>
          <h1 className="ye-display ye-h1" style={{ margin: "8px 0 18px" }}>The Full EPC Sequence, Under One Contractor</h1>
          <p style={{ maxWidth: 880, fontSize: 17, color: "rgba(255,255,255,0.9)" }}>
            Engineering support, procurement assistance, construction, installation and commissioning — scoped to your package, executed with our own fleet and teams.
          </p>
          <div style={{ marginTop: 30 }}>
            <ScopeRail slim showCaption={false} />
          </div>
        </div>
      </section>

      {SECTIONS.map((s, i) => {
        const Icon = s.icon;
        const bg = i % 2 === 0 ? "" : "ye-section--bg";
        return (
          <section key={s.id} id={s.id} className={`ye-section ${bg}`}>
            <div className="ye-container">
              <div className="ye-service-row">
                <div className="ye-service-icon"><Icon size={32} /></div>
                <div>
                  <span className="ye-eyebrow">{`0${i+1}`} · Service</span>
                  <h2 className="ye-display ye-h2" style={{ margin: "4px 0 10px" }}>{s.title}</h2>
                  <p style={{ margin: 0, fontStyle: "italic", color: "var(--ink)", fontSize: 17 }}>{s.intro}</p>
                  <p style={{ margin: "22px 0 8px", fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 13 }}>Scope includes</p>
                  <ul className="ye-service-list">
                    {s.list.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <CTABanner />
    </>
  );
}
